import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { apiService } from '../Config/api.service';
import { signalerErreur } from '../Utils/signalerErreur';

// Une notification n'est pas urgente à la seconde : au changement de page, au
// retour sur la fenêtre, et chaque minute pour ce qui arrive pendant qu'on lit.
const INTERVALLE_RELECTURE = 60000;

// Sans fournisseur — un écran monté seul, dans un test —, pas de cloche.
const NotificationsContexte = createContext(null);

/**
 * Les notifications persistantes du compte connecté (Yros/Kavero#21). L'état
 * lu/non-lu vit dans la base : le compteur se relit côté serveur, rien n'est
 * gardé d'une page à l'autre.
 *
 * `nonLues` est `null` tant que rien n'a été lu ; la liste, elle, ne se charge
 * qu'à l'ouverture de la cloche — le compteur suffit à la pastille.
 */
export const NotificationsProvider = ({ actif, children }) => {
  const { pathname } = useLocation();
  const [nonLues, setNonLues] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const derniereLecture = useRef(0);
  // La liste courante, lisible depuis `marquerLues` sans la mettre en dépendance.
  const notificationsRef = useRef([]);
  notificationsRef.current = notifications;

  const relire = useCallback(async () => {
    if (!actif) return;
    const numero = ++derniereLecture.current;
    try {
      const reponse = await apiService.compterNotifications();
      // Deux lectures peuvent se croiser : seule la dernière compte.
      if (numero !== derniereLecture.current) return;
      setNonLues(reponse?.ok ? reponse.non_lues : null);
    } catch (e) {
      // Un compteur indisponible se tait : il ne bloque rien.
    }
  }, [actif]);

  const charger = useCallback(async () => {
    if (!actif) return;
    try {
      const reponse = await apiService.listerNotifications();
      if (!reponse?.ok) return;
      setNotifications(reponse.notifications);
      setNonLues(reponse.non_lues);
    } catch (e) {
      // La liste précédente reste affichée.
    }
  }, [actif]);

  /**
   * Marque lues, en optimiste : la pastille ne doit pas attendre le réseau.
   * Si le serveur échoue, le compteur est relu plutôt que de mentir.
   */
  const marquerLues = useCallback(async (corps) => {
    const visees = corps.toutes ? null : new Set(corps.ids);
    const concernee = (n) => !n.lu && (visees === null || visees.has(n.id));
    const retirees = notificationsRef.current.filter(concernee).length;
    setNotifications((liste) => liste.map((n) => (concernee(n) ? { ...n, lu: true } : n)));
    setNonLues((avant) => {
      if (avant === null) return avant;
      return visees === null ? 0 : Math.max(avant - retirees, 0);
    });
    try {
      const reponse = await apiService.marquerNotificationsLues(corps);
      if (reponse?.ok) setNonLues(reponse.non_lues);
      else relire();
    } catch (e) {
      signalerErreur(e, 'NotificationsProvider.marquerLues');
      relire();
      charger();
    }
  }, [relire, charger]);

  useEffect(() => {
    if (!actif) {
      setNonLues(null);
      setNotifications([]);
      return;
    }
    relire();
  }, [actif, pathname, relire]);

  useEffect(() => {
    if (!actif) return undefined;
    const minuterie = setInterval(relire, INTERVALLE_RELECTURE);
    window.addEventListener('focus', relire);
    return () => {
      clearInterval(minuterie);
      window.removeEventListener('focus', relire);
    };
  }, [actif, relire]);

  const valeur = useMemo(
    () => (actif ? { nonLues, notifications, charger, marquerLues } : null),
    [actif, nonLues, notifications, charger, marquerLues]
  );
  return <NotificationsContexte.Provider value={valeur}>{children}</NotificationsContexte.Provider>;
};

/**
 * `{ nonLues, notifications, charger, marquerLues }`, ou `null` hors connexion :
 * la cloche ne se montre pas.
 */
export const useNotifications = () => useContext(NotificationsContexte);
